import { Box, Hidden, Theme, Typography } from '@mui/material';

import { Skeleton } from '@/components';
import { Routes } from '@/constants';
import { Link } from '@/containers';
import { AnalyticsService, EVENT_CONSTANTS } from '@/services';
import {
  selectActiveChapterId,
  selectActiveEpisodeId,
  selectEpisodeCompliance,
  selectIsChangingChapter,
  useAppSelector,
  useFetchChapterQuery,
  useFetchEpisodeQuery,
} from '@/store';
import { Location, Referrer } from '@/types';
import { createRouteWithParams } from '@/utils';

export const ChapterInfo = ({ referrer }: { referrer: Referrer }) => {
  const activeEpisodeId = useAppSelector(selectActiveEpisodeId);
  const activeChapterId = useAppSelector(selectActiveChapterId);
  const isChangingChapter = useAppSelector(selectIsChangingChapter);
  const compliance = useAppSelector(state =>
    selectEpisodeCompliance(state, activeEpisodeId),
  );

  const { data: episode, isLoading: isEpisodeLoading } = useFetchEpisodeQuery({
    episodeId: activeEpisodeId!,
    compliance,
  });
  const { data: chapter, isLoading: isChapterLoading } = useFetchChapterQuery({
    chapterId: activeChapterId!,
    compliance,
  });

  const clickEpisodeDetails = (location: Location) => {
    AnalyticsService.trackPlayerViewEpisode({
      chapterId: activeChapterId!,
      episodeId: activeEpisodeId!,
      location: { ...referrer, ...location },
      referrer: {
        ...referrer,
        component: EVENT_CONSTANTS.COMPONENT.TRACK_PLAYER,
      },
    });
  };

  const isLoading = isChapterLoading || isEpisodeLoading || isChangingChapter;

  return (
    <Box sx={styles.container}>
      <Box sx={styles.coverContainer}>
        {isLoading ? (
          <Skeleton variant="rectangular" />
        ) : (
          <Link
            to={{
              pathname: createRouteWithParams(Routes.EPISODE, {
                episodeId: activeEpisodeId,
              }),
            }}
            state={{
              referrer: {
                ...referrer,
                button: EVENT_CONSTANTS.EPISODE_IMAGE,
                component: EVENT_CONSTANTS.COMPONENT.TRACK_PLAYER,
              },
            }}
            onClick={() => {
              clickEpisodeDetails({
                button: EVENT_CONSTANTS.EPISODE_IMAGE,
                component: EVENT_CONSTANTS.COMPONENT.TRACK_PLAYER,
              });
            }}>
            <Box
              component="img"
              sx={styles.cover}
              src={`${episode?.cover}`}
              alt="episode cover"
            />
          </Link>
        )}
      </Box>
      <Hidden mdDown>
        <Box sx={styles.infoContainer}>
          {isLoading ? (
            <>
              <Skeleton height={32} />
              <Skeleton height={24} width={120} />
            </>
          ) : (
            <>
              <Link
                color="white"
                to={{
                  pathname: createRouteWithParams(Routes.EPISODE, {
                    episodeId: activeEpisodeId,
                  }),
                }}
                state={{
                  referrer: {
                    ...referrer,
                    button: EVENT_CONSTANTS.EPISODE_TITLE,
                    component: EVENT_CONSTANTS.COMPONENT.TRACK_PLAYER,
                  },
                }}
                onClick={() => {
                  clickEpisodeDetails({
                    button: EVENT_CONSTANTS.EPISODE_TITLE,
                    component: EVENT_CONSTANTS.COMPONENT.TRACK_PLAYER,
                  });
                }}>
                <Typography
                  variant="body2SemiBold"
                  gutterBottom
                  noWrap
                  sx={{ color: 'text.contrast' }}>
                  {episode?.name}
                </Typography>
              </Link>
              <Typography
                variant="body3"
                noWrap
                sx={{ color: 'text.contrast' }}>
                {chapter?.isTrailer ? 'Trailer' : chapter?.name}
              </Typography>
            </>
          )}
        </Box>
      </Hidden>
    </Box>
  );
};

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'row',
  },
  coverContainer: (theme: Theme) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: theme.dimensions.miniPlayer.height,
    width: theme.dimensions.miniPlayer.height,
  }),
  cover: (theme: Theme) => ({
    display: 'block',
    height: theme.dimensions.miniPlayer.height,
    width: theme.dimensions.miniPlayer.height,
    objectFit: 'cover',
    [theme.breakpoints.down('sm')]: {
      padding: 1,
      borderRadius: 5,
    },
  }),
  infoContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    pl: 2.5,
    flexGrow: 1,
    overflow: 'hidden',
    height: {
      xs: 66,
      sm: 'auto',
    },
  },
};
